import { useState } from 'react';
import ServiceNavbar from '../components/ServiceNavbar';
import Footer from '../../home/components/Footer';
import { Link } from 'react-router-dom';
import BrandingEnquiryForm from './components/BrandingEnquiryForm';

export default function Branding() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How long does the branding process take?',
      answer: 'The timeline varies by package. Essential branding typically takes 2-3 weeks, Professional packages 4-6 weeks, and Enterprise projects 8-12 weeks. We\'ll provide a detailed timeline during our discovery session based on your specific needs and deliverables.'
    },
    {
      question: 'What\'s the difference between a logo and a brand identity?',
      answer: 'A logo is just one element of your brand—it\'s the visual symbol. A brand identity is the complete system including your logo, colors, typography, imagery style, voice, messaging, and guidelines for how everything works together. Think of the logo as your face, and brand identity as your entire personality.'
    },
    {
      question: 'Do I need to have a clear vision before starting?',
      answer: 'Not at all! Many clients come to us with just a rough idea. Our discovery process is designed to uncover your brand\'s essence through strategic questions, competitor analysis, and collaborative workshops. We\'ll help you clarify your vision and translate it into a powerful brand.'
    },
    {
      question: 'How many logo concepts will I see?',
      answer: 'This depends on your package—Essential includes 3 concepts, Professional includes 5, and Enterprise offers unlimited concepts. Each concept is a distinct creative direction, and we\'ll refine your chosen direction through multiple revision rounds.'
    },
    {
      question: 'What file formats will I receive?',
      answer: 'You\'ll receive a complete logo package with all formats needed for any application: vector files (AI, EPS, SVG) for print and scaling, raster files (PNG, JPG) for digital use, and PDF versions. We also provide variations for different backgrounds and use cases.'
    },
    {
      question: 'Can you match my brand to my existing website or materials?',
      answer: 'Absolutely! If you have existing brand elements you want to keep, we can build around them. We can also create a refreshed brand that evolves your current identity while maintaining recognition. Just share your existing materials during our discovery phase.'
    },
    {
      question: 'What if I don\'t like the initial concepts?',
      answer: 'That\'s what revisions are for! We encourage honest feedback. If the initial concepts miss the mark, we\'ll dig deeper into what\'s not working and create new directions. Our goal is your complete satisfaction, and we won\'t stop until we get there.'
    },
    {
      question: 'Will I own the rights to my brand assets?',
      answer: 'Yes, 100%. Upon final payment, you receive full ownership and copyright of all brand assets we create for you. You\'re free to use, modify, and reproduce your brand materials however you need without any additional licensing fees.'
    }
  ];

  const features = [
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Logo Design',
      description: 'Distinctive, memorable logos that capture your brand essence and stand out in any context.'
    },
    {
      icon: 'ri-book-3-line',
      title: 'Brand Guidelines',
      description: 'Comprehensive style guides ensuring consistent brand application across all touchpoints.'
    },
    {
      icon: 'ri-eye-line',
      title: 'Visual Identity',
      description: 'Complete visual systems including imagery style, iconography, and graphic elements.'
    },
    {
      icon: 'ri-chat-quote-line',
      title: 'Brand Messaging',
      description: 'Compelling brand voice, taglines, and messaging frameworks that resonate with your audience.'
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Palette Development',
      description: 'Strategic color systems that evoke the right emotions and differentiate your brand.'
    },
    {
      icon: 'ri-font-size-2',
      title: 'Typography Selection',
      description: 'Carefully curated typefaces that reflect your brand personality and ensure readability.'
    }
  ];

  const deliverables = [
    { icon: 'ri-file-pdf-2-line', name: 'Brand Guidelines PDF', desc: 'Complete usage manual' },
    { icon: 'ri-folder-zip-line', name: 'Logo Package', desc: 'All formats & variations' },
    { icon: 'ri-pantone-line', name: 'Color Specifications', desc: 'HEX, RGB, CMYK, Pantone' },
    { icon: 'ri-text', name: 'Typography Kit', desc: 'Font files & pairings' },
    { icon: 'ri-image-line', name: 'Brand Assets', desc: 'Icons, patterns, graphics' },
    { icon: 'ri-presentation-line', name: 'Brand Deck', desc: 'Presentation templates' }
  ];

  const scrollToEnquiry = () => {
    document.getElementById('branding-enquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <ServiceNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center pt-28">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/65e867970c32dbb7105b148ded8f8ebe.jpg"
            alt="Branding Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00989e]/20 rounded-full mb-6">
              <i className="ri-pencil-ruler-2-line text-[#00989e]"></i>
              <span className="text-[#00b4a6] text-sm font-medium">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Branding
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Comprehensive brand identity development that captures your essence and creates lasting connections with your audience. From logo design to complete brand systems.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToEnquiry}
                className="px-8 py-4 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap"
              >
                Start Your Brand
              </button>
              <Link to="/case-studies" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              What's Included
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to build a powerful, cohesive brand identity
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#00989e]/10 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-[#00989e]`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Deliverables Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Brand Deliverables
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A complete brand toolkit ready for immediate implementation
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {deliverables.map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 flex flex-col items-center justify-center aspect-square hover:scale-105 transition-transform">
                  <i className={`${item.icon} text-4xl text-[#00989e] mb-3`}></i>
                  <span className="text-white font-medium text-sm text-center">{item.name}</span>
                  <span className="text-gray-400 text-xs text-center mt-1">{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Branding Process
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A strategic approach to building brands that resonate
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Deep dive into your vision, values, and target audience' },
              { step: '02', title: 'Strategy', desc: 'Define positioning, messaging, and brand architecture' },
              { step: '03', title: 'Design', desc: 'Create visual identity and brand elements' },
              { step: '04', title: 'Deliver', desc: 'Complete brand guidelines and asset package' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-bold text-[#00989e]/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Branding Matters Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Why Branding Matters
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Your brand is more than a logo—it's the complete experience people have with your business. A strong brand builds trust, creates recognition, and drives customer loyalty.
              </p>
              <div className="space-y-4">
                {[
                  { stat: '77%', text: 'of consumers buy based on brand name' },
                  { stat: '59%', text: 'prefer to buy from brands they trust' },
                  { stat: '3x', text: 'more revenue for consistent brands' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-[#00989e]">{item.stat}</div>
                    <div className="text-gray-600">{item.text}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Creative%20brand%20identity%20design%20presentation%20with%20logo%20variations%20business%20cards%20letterhead%20and%20brand%20collateral%20mockups%20arranged%20beautifully%20on%20a%20clean%20surface%2C%20teal%20accent%20colors%2C%20professional%20photography%20style%2C%20minimalist%20modern%20aesthetic%20with%20soft%20shadows&width=600&height=500&seq=branding-why-1&orientation=landscape"
                alt="Brand Identity Presentation"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Branding Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the perfect branding package to elevate your business identity
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Essential Package */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100">
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-pencil-line text-2xl text-slate-700"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Essential</h3>
                <p className="text-gray-500 text-sm">Perfect for startups</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$1,800</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Primary logo design',
                  '3 initial concepts',
                  'Color palette (3 colors)',
                  'Typography selection',
                  'Logo variations (horizontal, stacked)',
                  'Basic brand guidelines (PDF)',
                  '2 rounds of revisions'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={scrollToEnquiry}
                className="w-full py-3 border-2 border-slate-900 text-slate-900 font-semibold rounded-full hover:bg-slate-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Started
              </button>
            </div>

            {/* Professional Package */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 shadow-xl relative transform md:scale-105">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-[#00989e] text-white text-sm font-medium rounded-full whitespace-nowrap">Most Popular</span>
              </div>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-[#00989e]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl text-[#00989e]"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
                <p className="text-gray-400 text-sm">For growing businesses</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">$4,500</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Complete logo system',
                  '5 initial concepts',
                  'Extended color palette (5 colors)',
                  'Primary & secondary typography',
                  'All logo variations & formats',
                  'Comprehensive brand guidelines',
                  'Business card design',
                  'Social media kit',
                  'Brand presentation deck',
                  '4 rounds of revisions'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={scrollToEnquiry}
                className="w-full py-3 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Started
              </button>
            </div>

            {/* Enterprise Package */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100">
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-2xl text-slate-700"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Enterprise</h3>
                <p className="text-gray-500 text-sm">Complete brand transformation</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$9,000</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Full brand identity system',
                  'Unlimited concepts',
                  'Complete color system',
                  'Custom typography selection',
                  'Brand voice & messaging',
                  'Stationery suite design',
                  'Marketing collateral templates',
                  'Packaging design guidelines',
                  'Brand launch strategy',
                  'Unlimited revisions',
                  '60 days post-launch support'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={scrollToEnquiry}
                className="w-full py-3 border-2 border-slate-900 text-slate-900 font-semibold rounded-full hover:bg-slate-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </button>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Need a custom solution? <Link to="/contact" className="text-[#00989e] font-medium hover:underline cursor-pointer">Contact us</Link> for a personalized quote.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Got questions about branding? Here's everything you need to know about our brand identity services.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-gray-200 rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#00989e]/10 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-[#00989e] text-xl"></i>
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="p-6 pt-0 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Link to="/contact" className="inline-flex items-center gap-2 text-[#00989e] font-semibold hover:underline cursor-pointer">
              <span>Contact our team</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Enquiry Form - replaces old CTA */}
      <BrandingEnquiryForm />

      <Footer />
    </div>
  );
}
